/** One continuously retargeted animation, rather than a new smooth scroll per token. */
export class ChatScrollController {
  private following = true
  private streaming = false
  private dragging = false
  private frame: number | null = null
  private lastFrame = 0
  private lastAutoTop = 0
  private paused = false
  private destroyed = false
  private readonly doc: Document
  private readonly view: Window

  constructor(private readonly viewport: HTMLElement, private readonly onPaused: (paused: boolean) => void) {
    this.lastAutoTop = viewport.scrollTop
    this.doc = viewport.ownerDocument
    this.view = this.doc.defaultView!
    viewport.addEventListener('scroll', this.onScroll, { passive: true })
    viewport.addEventListener('wheel', this.onWheel, { passive: true })
    viewport.addEventListener('touchmove', this.onTouchMove, { passive: true })
    viewport.addEventListener('keydown', this.onKeyDown)
    // Native scrollbar events vary between engines. Listen in capture phase to
    // both pointer and mouse events, and catch release outside the viewport.
    this.doc.addEventListener('pointerdown', this.onPointerDown, true)
    this.doc.addEventListener('mousedown', this.onPointerDown, true)
    this.doc.addEventListener('pointerup', this.onRelease, true)
    this.doc.addEventListener('mouseup', this.onRelease, true)
    this.doc.addEventListener('pointercancel', this.onRelease, true)
    this.doc.addEventListener('pointermove', this.onPointerMove, true)
    this.doc.addEventListener('mousemove', this.onPointerMove, true)
    this.doc.addEventListener('selectionchange', this.onSelection)
    this.view.addEventListener('blur', this.onRelease)
  }

  setStreaming(value: boolean): void {
    const starting = value && !this.streaming
    this.streaming = value
    if (starting && !this.dragging) this.follow()
  }

  contentChanged(): void {
    if (this.destroyed) return
    this.publishPaused()
    if (this.following && !this.dragging && this.frame === null && this.viewport.clientHeight > 0) {
      this.lastFrame = 0
      this.frame = requestAnimationFrame(this.animate)
    }
  }

  follow = (): void => {
    this.following = true
    this.contentChanged()
  }

  jumpToLatest = (): void => {
    this.cancel(); this.following = true
    this.writeTop(this.bottom())
    this.publishPaused()
  }

  jumpTo(element: HTMLElement): void {
    this.pause()
    const top = element.getBoundingClientRect().top - this.viewport.getBoundingClientRect().top + this.viewport.scrollTop
    this.viewport.scrollTop = Math.max(0, top - 12)
    this.publishPaused()
  }

  private writeTop(value: number): void { this.viewport.scrollTop = value; this.lastAutoTop = this.viewport.scrollTop }
  private bottom(): number { return Math.max(0, this.viewport.scrollHeight - this.viewport.clientHeight) }
  private publishPaused(): void {
    const value = !this.following && this.bottom() - this.viewport.scrollTop > 8
    if (value !== this.paused) { this.paused = value; this.onPaused(value) }
  }
  private cancel(): void {
    if (this.frame !== null) cancelAnimationFrame(this.frame)
    this.frame = null; this.lastFrame = 0
  }
  private pause(): void {
    this.following = false; this.cancel(); this.publishPaused()
  }
  private animate = (now: number): void => {
    this.frame = null
    if (this.destroyed || this.dragging || !this.following || this.viewport.clientHeight <= 0) return
    const target = this.bottom(), current = this.viewport.scrollTop, gap = target - current
    if (gap <= 0) return
    if (this.view.matchMedia?.('(prefers-reduced-motion: reduce)').matches || gap < 0.75) {
      this.writeTop(target)
      return
    }
    const dt = this.lastFrame ? Math.min(64, Math.max(1, now - this.lastFrame)) : 16
    this.lastFrame = now
    // Exponential easing retains velocity when new tokens arrive. A speed cap
    // also keeps returning from a long answer controlled, with no overshoot.
    const step = Math.min(gap * (1 - Math.exp(-dt / 130)), Math.max(1600, this.viewport.clientHeight * 6) * dt / 1000)
    this.writeTop(Math.min(target, current + Math.max(0.75, step)))
    this.frame = requestAnimationFrame(this.animate)
  }
  private onScroll = (): void => {
    // Some native overlay scrollbars omit DOM pointer events. An external
    // scroll away from the bottom must still stop the running animation.
    if (!this.dragging && this.following && Math.abs(this.viewport.scrollTop - this.lastAutoTop) > 2 && this.bottom() - this.viewport.scrollTop > 4) this.pause()
    if (!this.dragging && !this.following && this.bottom() - this.viewport.scrollTop <= 4) this.follow()
    else this.publishPaused()
  }
  private onWheel = (event: WheelEvent): void => { if (event.deltaY) this.pause() }
  private onTouchMove = (): void => { this.pause() }
  private onKeyDown = (event: KeyboardEvent): void => {
    const target = event.target as HTMLElement | null
    if (target?.closest('input,textarea,select,button,[contenteditable="true"]')) return
    if (['ArrowUp','ArrowDown','PageUp','PageDown','Home','End',' '].includes(event.key)) this.pause()
  }
  private onPointerDown = (event: MouseEvent | PointerEvent): void => {
    if (this.dragging || event.button !== 0 || ('pointerType' in event && event.pointerType !== 'mouse')) return
    if (event.target !== this.doc && !this.viewport.contains(event.target as Node)) return
    const rect = this.viewport.getBoundingClientRect()
    const gutter = Math.max(16, this.viewport.offsetWidth - this.viewport.clientWidth)
    const rtl = this.view.getComputedStyle(this.viewport).direction === 'rtl'
    const inScrollbar = rtl ? event.clientX >= rect.left && event.clientX <= rect.left + gutter
      : event.clientX <= rect.right && event.clientX >= rect.right - gutter
    if (!inScrollbar || event.clientY < rect.top || event.clientY > rect.bottom || this.bottom() <= 0) return
    this.dragging = true; this.pause()
  }
  private onRelease = (): void => {
    if (!this.dragging) return
    this.dragging = false
    this.publishPaused()
  }
  private onPointerMove = (event: MouseEvent | PointerEvent): void => {
    if (this.dragging && event.buttons === 0) this.onRelease()
  }
  private onSelection = (): void => {
    const selection = this.doc.getSelection()
    if (selection && !selection.isCollapsed && selection.anchorNode && this.viewport.contains(selection.anchorNode)) this.pause()
  }

  destroy(): void {
    this.destroyed = true; this.cancel()
    this.viewport.removeEventListener('scroll', this.onScroll)
    this.viewport.removeEventListener('wheel', this.onWheel)
    this.viewport.removeEventListener('touchmove', this.onTouchMove)
    this.viewport.removeEventListener('keydown', this.onKeyDown)
    this.doc.removeEventListener('pointerdown', this.onPointerDown, true)
    this.doc.removeEventListener('mousedown', this.onPointerDown, true)
    this.doc.removeEventListener('pointerup', this.onRelease, true)
    this.doc.removeEventListener('mouseup', this.onRelease, true)
    this.doc.removeEventListener('pointercancel', this.onRelease, true)
    this.doc.removeEventListener('pointermove', this.onPointerMove, true)
    this.doc.removeEventListener('mousemove', this.onPointerMove, true)
    this.doc.removeEventListener('selectionchange', this.onSelection)
    this.view.removeEventListener('blur', this.onRelease)
  }
}
